import { useEffect, useMemo, useState } from "react";
import { DndContext, PointerSensor, useDraggable, useDroppable, useSensor, useSensors } from "@dnd-kit/core";
import { Link } from "react-router-dom";
import { CalendarRange, Filter, Loader2, Plus, Wrench } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { JobCardAPI, TechnicianAPI, BranchAPI, extractError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { formatRelativeTime, formatDuration } from "@/lib/format";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const COLUMNS = [
    { key: "booked",            label: "Booked",            color: "border-zinc-400" },
    { key: "in_progress",       label: "In progress",       color: "border-sky-500" },
    { key: "awaiting_approval", label: "Awaiting approval", color: "border-violet-500" },
    { key: "awaiting_parts",    label: "Awaiting parts",    color: "border-blue-500" },
    { key: "qc",                label: "QC",                color: "border-indigo-500" },
    { key: "ready",             label: "Ready",             color: "border-emerald-500" },
    { key: "delivered",         label: "Delivered",         color: "border-emerald-700" },
    { key: "cancelled",         label: "Cancelled",         color: "border-rose-500" },
];

export default function JobsPage() {
    const { user, permissions } = useAuth();
    const canDrag = permissions.includes("jobcard:transition");

    const [items, setItems] = useState([]);
    const [counts, setCounts] = useState({});
    const [techs, setTechs] = useState([]);
    const [branches, setBranches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({ q: "", technician_user_id: "", branch_id: "" });
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

    async function load() {
        setLoading(true);
        try {
            const params = { page_size: 200 };
            if (filters.q) params.q = filters.q;
            if (filters.technician_user_id) params.technician_user_id = filters.technician_user_id;
            if (filters.branch_id) params.branch_id = filters.branch_id;
            const [list, c] = await Promise.all([JobCardAPI.list(params), JobCardAPI.counts(params.branch_id ? { branch_id: params.branch_id } : {})]);
            setItems(list.items || []);
            setCounts(c);
        } catch (e) {
            toast.error(extractError(e));
        } finally { setLoading(false); }
    }

    useEffect(() => {
        BranchAPI.list().then(setBranches).catch(() => setBranches([]));
        TechnicianAPI.list({ is_active: true }).then((r) => setTechs(r.items || [])).catch(() => setTechs([]));
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters.q, filters.technician_user_id, filters.branch_id]);

    const grouped = useMemo(() => {
        const out = Object.fromEntries(COLUMNS.map((c) => [c.key, []]));
        for (const j of items) (out[j.status] || []).push(j);
        return out;
    }, [items]);

    async function handleDrop(jobId, newStatus) {
        const j = items.find((x) => x.id === jobId);
        if (!j || j.status === newStatus) return;
        // Optimistic patch
        const prevItems = items;
        setItems((p) => p.map((x) => (x.id === jobId ? { ...x, status: newStatus } : x)));
        try {
            const next = await JobCardAPI.transition(jobId, { to: newStatus, version: j.version });
            setItems((p) => p.map((x) => (x.id === jobId ? { ...x, ...next } : x)));
            toast.success(`Moved ${next.job_number} → ${newStatus}`);
            JobCardAPI.counts(filters.branch_id ? { branch_id: filters.branch_id } : {}).then(setCounts).catch(() => {});
        } catch (e) {
            setItems(prevItems); // revert
            toast.error(extractError(e));
        }
    }

    return (
        <div className="space-y-5 animate-fade-in" data-testid="jobs-page">
            <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <h1 className="font-display text-3xl font-extrabold tracking-tight sm:text-4xl">Job cards</h1>
                    <p className="mt-1 text-sm text-zinc-500">Drag to move across the lifecycle. Stage age is shown live.</p>
                </div>
                <div className="flex items-center gap-2">
                    <Link to="/schedule"><Button variant="outline" size="sm"><CalendarRange className="mr-1 h-4 w-4" /> Schedule</Button></Link>
                    <Link to="/jobs/new"><Button size="sm" className="bg-brand-red text-white hover:bg-brand-redDeep" data-testid="jobs-new"><Plus className="mr-1 h-4 w-4" /> Walk-in</Button></Link>
                </div>
            </header>

            <Card className="p-3">
                <div className="flex flex-wrap items-center gap-2">
                    <Filter className="h-4 w-4 text-zinc-400" />
                    <Input className="h-8 max-w-xs" placeholder="Search job#, reg, customer…" value={filters.q}
                           onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} data-testid="jobs-search" />
                    {user?.role === "owner" && (
                        <select className="h-8 rounded-md border bg-background px-2 text-sm" value={filters.branch_id}
                                onChange={(e) => setFilters((f) => ({ ...f, branch_id: e.target.value }))} data-testid="jobs-branch">
                            <option value="">All branches</option>
                            {branches.map((b) => <option key={b.id} value={b.id}>{b.code}</option>)}
                        </select>
                    )}
                    <select className="h-8 rounded-md border bg-background px-2 text-sm" value={filters.technician_user_id}
                            onChange={(e) => setFilters((f) => ({ ...f, technician_user_id: e.target.value }))} data-testid="jobs-tech">
                        <option value="">All technicians</option>
                        {techs.map((t) => <option key={t.user_id} value={t.user_id}>{t.user?.name || t.employee_code}</option>)}
                    </select>
                    {loading && <Loader2 className="ml-auto h-4 w-4 animate-spin text-zinc-400" />}
                </div>
            </Card>

            <DndContext sensors={sensors} onDragEnd={(e) => {
                if (e.over && e.active) handleDrop(String(e.active.id), String(e.over.id));
            }}>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-8">
                    {COLUMNS.map((col) => (
                        <KanbanColumn key={col.key} col={col} count={counts[col.key] || 0} items={grouped[col.key]} canDrag={canDrag} />
                    ))}
                </div>
            </DndContext>
        </div>
    );
}

function KanbanColumn({ col, count, items, canDrag }) {
    const { setNodeRef, isOver } = useDroppable({ id: col.key });
    return (
        <div className="flex flex-col" data-testid={`kanban-column-${col.key}`}>
            <div className={cn("flex items-center justify-between rounded-t-md border-t-2 border-x border-b bg-zinc-50 px-2.5 py-1.5 text-[11px] font-mono uppercase tracking-wider dark:bg-zinc-900", col.color)}>
                <span>{col.label}</span>
                <span className="rounded bg-white px-1.5 py-0.5 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300">{count}</span>
            </div>
            <div
                ref={setNodeRef}
                className={cn(
                    "flex min-h-[60vh] flex-col gap-2 rounded-b-md border-x border-b bg-white p-2 dark:bg-zinc-950",
                    isOver && "ring-2 ring-brand-red/40"
                )}
            >
                {items.length === 0 && <div className="grid h-24 place-items-center text-[11px] text-zinc-400">empty</div>}
                {items.map((j) => <KanbanCard key={j.id} j={j} canDrag={canDrag} />)}
            </div>
        </div>
    );
}

function KanbanCard({ j, canDrag }) {
    const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: j.id, disabled: !canDrag });
    const style = transform ? { transform: `translate(${transform.x}px, ${transform.y}px)` } : undefined;
    const enteredAt = j.stage_history?.length ? j.stage_history[j.stage_history.length - 1]?.at : j.opened_at;
    const stale = (j.status === "awaiting_parts" || j.status === "awaiting_approval")
        && enteredAt && (Date.now() - new Date(enteredAt).getTime()) > 24 * 3600 * 1000;
    return (
        <Link
            to={`/jobs/${j.id}`}
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}
            className={cn(
                "block rounded-md border bg-white p-2.5 text-xs shadow-sm hover:shadow-md dark:bg-zinc-900",
                isDragging && "opacity-50 ring-2 ring-brand-red/50",
                canDrag ? "cursor-grab active:cursor-grabbing" : ""
            )}
            data-testid={`job-card-kanban-${j.job_number}`}
            data-job-id={j.id}
        >
            <div className="flex items-center justify-between">
                <span className="font-mono text-[10px] text-zinc-500">{j.job_number}</span>
                {stale && <span className="rounded bg-brand-red px-1 text-[9px] uppercase tracking-wider text-white">stale</span>}
            </div>
            <div className="mt-1 truncate font-semibold">{j.customer?.display_name || "Walk-in"}</div>
            <div className="font-mono text-[10px] text-zinc-500">{j.vehicle?.registration_number || "—"}</div>
            <div className="mt-1 flex items-center justify-between text-[10px]">
                <span className="font-mono text-zinc-500">{formatRelativeTime(enteredAt)}</span>
                {j.total_tat_seconds != null && <span className="font-mono text-zinc-500">{formatDuration(j.total_tat_seconds)}</span>}
            </div>
        </Link>
    );
}
